import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/react-use-analytics-api/react-use-analytics-api/node_modules/gatsby-theme-docz/src/base/Layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1>useViewSelector</h1>
    <h2 {...{
      "id": "description"
    }}>{`Description`}</h2>
    <p>{`This hook renders a `}<a parentName="p" {...{
        "href": "https://developers.google.com/analytics/devguides/reporting/embed/v1/component-reference#viewselector"
      }}>{`ViewSelector`}</a>{`
into a container on the page using the `}<a parentName="p" {...{
        "href": "/react-use-analytics-api/useAnalyticsApi"
      }}>{`Google Analytics Embed API`}</a>{`.`}</p>
    <p>{`The view selector can be used to change the Google Analytics view for data queries when `}<a parentName="p" {...{
        "href": "/react-use-analytics-api/useDataChart"
      }}>{`rendering charts`}</a>{`.`}</p>
    <h2 {...{
      "id": "usage"
    }}>{`Usage`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { useViewSelector } from "react-use-analytics-api";
`}</code></pre>
    <p>{`In your function component:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const [viewId, setViewId] = React.useState();
useViewSelector(gapi, "my-view-selector-container", (viewId) =>
  setViewId(viewId)
);
`}</code></pre>
    <p>{`Provide the hook with the loaded `}<a parentName="p" {...{
        "href": "/react-use-analytics-api/useAnalyticsApi"
      }}>{`Google Analytics Embed API`}</a>{` (`}<inlineCode parentName="p">{`gapi`}</inlineCode>{`), the ID of the container DOM element to render to,
and a callback for when the view changes.`}</p>
    <p>{`If `}<inlineCode parentName="p">{`gapi`}</inlineCode>{` is `}<inlineCode parentName="p">{`undefined`}</inlineCode>{`, nothing will be rendered.`}</p>
    <h2 {...{
      "id": "signature"
    }}>{`Signature`}</h2>
    <p><inlineCode parentName="p">{`useViewSelector( gapi?: GoogleAnalyticsEmbedAPI, viewSelectorId: string, onChange: (viewId: string) => void)) => void`}</inlineCode></p>
    <h3 {...{
      "id": "arguments"
    }}>{`Arguments`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Argument`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`gapi`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`GoogleAnalyticsEmbedAPI`}</inlineCode>{` `}{`|`}{` `}<inlineCode parentName="td">{`undefined`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The `}<a parentName="td" {...{
              "href": "/react-use-analytics-api/useAnalyticsApi"
            }}>{`Google Analytics Embed API`}</a>{`. When `}<inlineCode parentName="td">{`undefined`}</inlineCode>{`, nothing is rendered.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`viewSelectorId`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The ID of the DOM element to render the view selector to.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`onChange`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`(viewId: string) => void`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Called with the selected `}<inlineCode parentName="td">{`viewId`}</inlineCode>{` whenever the view selection changes. Will be called immediately.`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "return"
    }}>{`Return`}</h3>
    <p><inlineCode parentName="p">{`void`}</inlineCode>{` - The hook has no return.`}</p>
    <h2 {...{
      "id": "example"
    }}>{`Example`}</h2>
    <p>{`The following example shows use of a view selector to display the selected Google Analytics view ID.`}</p>
    <p>{`This example includes full auth flow as well.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import * as React from "react";
import {
  useAnalyticsApi,
  useAuthorize,
  useSignOut,
  useViewSelector,
} from "react-use-analytics-api";

export function UseViewSelectorExample(props) {
  const { ready, gapi, authorized, error } = useAnalyticsApi();
  const [viewId, setViewId] = React.useState();
  const viewSelectorContainerId = "view-selector-container";
  useViewSelector(
    authorized ? gapi : undefined,
    viewSelectorContainerId,
    (viewId) => setViewId(viewId)
  );

  // Workaround for API limitation - see useAuthorize docs
  const authDiv = React.useRef(null);
  const [authorizeCalled, setAuthorizeCalled] = React.useState(false);
  const hasAuthElements =
    authDiv.current && authDiv?.current?.children?.length > 0;

  const authorize = useAuthorize(gapi, {
    clientId: props.clientId,
    container: "authorize-container-id",
  });
  const signOut = useSignOut(gapi);

  React.useEffect(() => {
    if (ready && !error && !authorizeCalled) {
      authorize();
      setAuthorizeCalled(true);
    }
  }, [ready, error, authorizeCalled, authorize]);

  return (
    <div>
      {!ready && <div>Loading...</div>}
      {ready && (
        <div>
          {authorized && (
            <div>
              <div style={{ fontSize: "200%" }}>
                View ID:{" "}
                <span style={{ border: "solid 3px orange", padding: "5px" }}>
                  {viewId}
                </span>
              </div>
              <div id={viewSelectorContainerId} />
              <div>
                <button onClick={() => signOut()}>Sign Out</button>
              </div>
            </div>
          )}
          {!authorized && (
            <div>
              <div ref={authDiv} id="authorize-container-id"></div>
              {!hasAuthElements && <div>🔄 Refresh the page to sign in.</div>}
            </div>
          )}
        </div>
      )}
      {error && <div>{error.toString()}</div>}
    </div>
  );
}
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      